import { ActivityLogTab } from "@features/ax/shared/ActivityLog";
import { NotesTab } from "@features/ax/shared/NotesTab";
import { toast } from "@shared/hooks/use-toast";
import { Tabs } from "@shared/ui/tabs";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteFacility, useFacilityDetails } from "../../hooks";
import { DeleteFacility } from "../../shared/DeleteConfirmation";
import { FacilityKeyMetricsRow } from "./facilityKeyMatrics";
import { FacilityKeyStatsCard } from "./facilityKeyStatsCard";
import { FacilityProfileCard } from "./facilityProfileCard";
import { FacilityQuickActionsSidebar } from "./facilityQuickActionCard";
import { FacilityAlertsTab } from "./tabs/alerts/alerts";
import AssetsTab from "./tabs/asset/asset";
import { ComplianceLanding } from "./tabs/compliance/landing";
import { ConditionsRisksTab } from "./tabs/conditionRisk/conditionRisk";
import { FilesLanding } from "./tabs/files/landing";
import { FacilityFinanceTab } from "./tabs/finance/finance";
import NetworksTab from "./tabs/networks/network";
import { OMTab } from "./tabs/oAndM/oAndM";
import { FacilityPerformanceTab } from "./tabs/performance/performance";
import { SchedulesTab } from "./tabs/schedules/schedules";
import SystemsTab from "./tabs/system/system";

const urlMapping = {
  performance: "perf",
  alerts: "alerts",
  finance: "finance",
  compliance: "compliance",
  systems: "systems",
  networks: "networks",
  assets: "assets",
  "conditions-risks": "conditions",
  schedules: "schedules",
  files: "files",
  om: "operations",
  activityLog: "activity-log",
  notes: "notes",
};

export const FacilityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const source = "0";
  const [showDelete, setShowDelete] = useState(false);

  const { data: facility } = useFacilityDetails(id || "");
  const deleteFacilityMutation = useDeleteFacility();

  const handleDeleteConfirm = async () => {
    if (!facility) return;

    try {
      await deleteFacilityMutation.mutateAsync({
        facilityId: facility.id.toString(),
      });

      navigate("/ax/asset-registry/facilities");
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message || "Failed to delete facility",
        variant: "destructive",
      });
    } finally {
      setShowDelete(false);
    }
  };

  if (!facility) {
    return <div className="p-6"></div>;
  }

  const quickActions = [
    {
      label: "Edit Facility",
      icon: "edit",
      onClick: () => {
        navigate(`/ax/asset-registry/facilities/edit/${id}`);
      },
      variant: "outline" as const,
    },
    {
      label: "Create Service Order",
      icon: "file-text",
      onClick: () => {
        navigate("/ax/service-order/landing/create");
      },
      variant: "outline" as const,
    },
    {
      label: "Delete Facility",
      icon: "trash-2",
      onClick: () => {
        setShowDelete(true);
      },
      variant: "outline" as const,
    },
  ];

  const facilityTabs = {
    performance: {
      label: "Performance",
      component: <FacilityPerformanceTab facility={facility} />,
    },
    alerts: {
      label: "Alerts",
      component: <FacilityAlertsTab facility={facility} />,
    },
    finance: {
      label: "Finance",
      component: <FacilityFinanceTab facility={facility} />,
    },
    compliance: {
      label: "Compliance",
      component: <ComplianceLanding />,
    },
    systems: {
      label: "Systems",
      component: <SystemsTab facility={facility} />,
    },
    networks: {
      label: "Networks",
      component: <NetworksTab facility={facility} />,
    },
    assets: {
      label: "Assets",
      component: <AssetsTab facility={facility} />,
    },
    "conditions-risks": {
      label: "Conditions & Risks",
      component: <ConditionsRisksTab facility={facility} />,
    },
    schedules: {
      label: "Schedules",
      component: <SchedulesTab facility={facility} />,
    },
    om: {
      label: "O&M",
      component: <OMTab facility={facility} />,
    },
    files: {
      label: "Files & Documents",
      shortLabel: "Files",
      component: <FilesLanding data={facility?.uploadedDocuments} />,
    },
    notes: {
      label: "Notes",
      component: <NotesTab requestId={id} source={source} title="Facility" />,
    },
    activityLog: {
      label: "Activity Log",
      component: <ActivityLogTab entityId={id} entityType={source} />,
    },
  };

  return (
    <div className="p-4">
      <div className="space-y-6">
        <div className="space-y-6">
          <div className="flex gap-4">
            <div className="lg:col-span-4 space-y-6">
              <FacilityProfileCard data={facility} />
              <FacilityKeyMetricsRow data={facility} />
            </div>
            <div className="space-y-4">
              <FacilityKeyStatsCard data={facility} />
              <FacilityQuickActionsSidebar
                title="Quick Actions"
                description="Common facility management tasks"
                actions={quickActions}
              />
            </div>
          </div>
          <Tabs
            defaultValue="performance"
            urlMapping={urlMapping}
            tabComponents={facilityTabs}
            tabsListClassName="flex gap-4 w-full mb-6"
            idPrefix="facility-tab"
            level={0}
          />
        </div>
      </div>
      <DeleteFacility
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDeleteConfirm}
        facility={facility}
        isLoading={deleteFacilityMutation.isPending}
      />
    </div>
  );
};
